import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface Announcement {
  id: string;
  title: string;
  content?: string;
  status?: string;
  created_at: string;
  published_at?: string;
  images?: string[];
  external_link?: string;
  source?: 'wall_post' | 'event';
  source_id?: string;
}

export const useAnnouncements = () => {
  return useQuery<Announcement[]>({
    queryKey: ['announcements'],
    queryFn: async () => {
      const { data: wallPosts, error: wallPostsError } = await supabase
        .from('wall_posts')
        .select('*')
        .eq('type', 'home_announcement')
        .order('created_at', { ascending: false })
        .limit(100);

      if (wallPostsError) throw wallPostsError;

      const { data: eventAnnouncements, error: eventAnnouncementsError } = await supabase
        .from('events')
        .select('id, title, description, status, created_at, image_url')
        .or('category.ilike.%general announcement%,category.ilike.%general annoucement%')
        .order('created_at', { ascending: false })
        .limit(100);

      if (eventAnnouncementsError) throw eventAnnouncementsError;

      const wallPostAnnouncements: Announcement[] = (wallPosts ?? []).map((row) => ({
        ...(row as Announcement),
        source: 'wall_post',
        source_id: (row as { id: string }).id,
      }));

      const mappedEventAnnouncements: Announcement[] = (eventAnnouncements ?? []).map((row) => {
        const eventRow = row as {
          id: string;
          title: string;
          description?: string | null;
          status?: string | null;
          created_at: string;
          image_url?: string | null;
        };
        return {
          id: `event:${eventRow.id}`,
          title: eventRow.title,
          content: eventRow.description ?? '',
          status: eventRow.status ?? 'draft',
          created_at: eventRow.created_at,
          published_at: undefined,
          images: eventRow.image_url ? [eventRow.image_url] : [],
          external_link: undefined,
          source: 'event',
          source_id: eventRow.id,
        };
      });

      return [...mappedEventAnnouncements, ...wallPostAnnouncements].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
  });
};
